import type { ApiClient } from '../client';
import type {
  ApiResponse,
  CreateEnrollmentRequest,
  CreateEnrollmentResponse,
  CreateFeeWaiverApplicationRequest,
  CreateFeeWaiverApplicationResponse,
  CreateGuardianRequest,
  CreateGuardianResponse,
  CreateStudentDocumentRequest,
  CreateStudentDocumentResponse,
  CreateStudentLoginRequest,
  CreateStudentLoginResponse,
  CreateStudentRequest,
  CreateStudentResponse,
  ExecutePromotionRequest,
  ExecutePromotionResponse,
  ListAttendanceQuery,
  ListAttendanceResponse,
  ListEnrollmentsQuery,
  ListEnrollmentsResponse,
  ListFeeWaiverApplicationsResponse,
  ListGuardiansResponse,
  ListStudentAuditsResponse,
  ListStudentDocumentsResponse,
  ListStudentSubjectsResponse,
  ListStudentsQuery,
  PaginatedListStudentsResponse,
  PreviewPromotionRequest,
  PreviewPromotionResponse,
  ReviewFeeWaiverApplicationRequest,
  ReviewFeeWaiverApplicationResponse,
  SetStudentSubjectsRequest,
  SetStudentSubjectsResponse,
  SoftDeleteStudentRequest,
  UpdateStudentRequest,
} from '../types';
import { appendPaginationQuery } from '../../../types/pagination';

export function bindStudents(client: ApiClient) {
  return {
async listStudents(params?: ListStudentsQuery): Promise<ApiResponse<PaginatedListStudentsResponse>> {
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        const s = String(v).trim();
        if (!s) return;
        qs.set(k, s);
      });
    }
    const endpoint = qs.toString() ? `/students?${qs.toString()}` : '/students';
    return client.request<PaginatedListStudentsResponse>(endpoint);
  },

async createStudent(body: CreateStudentRequest): Promise<ApiResponse<CreateStudentResponse>> {
    return client.request<CreateStudentResponse>('/students', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async createStudentLogin(
    body: CreateStudentLoginRequest
  ): Promise<ApiResponse<CreateStudentLoginResponse>> {
    return client.request<CreateStudentLoginResponse>('/students/create-login', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async createEnrollment(body: CreateEnrollmentRequest): Promise<ApiResponse<CreateEnrollmentResponse>> {
    return client.request<CreateEnrollmentResponse>('/enrollments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listEnrollments(params?: ListEnrollmentsQuery): Promise<ApiResponse<ListEnrollmentsResponse>> {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    if (params?.student_id) qs.set('student_id', params.student_id);
    if (params?.class_id) qs.set('class_id', params.class_id);
    if (params?.academic_year) qs.set('academic_year', params.academic_year);
    const query = qs.toString();
    return client.request<ListEnrollmentsResponse>(query ? `/enrollments?${query}` : '/enrollments');
  },

async previewPromotion(body: PreviewPromotionRequest): Promise<ApiResponse<PreviewPromotionResponse>> {
    return client.request<PreviewPromotionResponse>('/enrollments/promotion/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async executePromotion(body: ExecutePromotionRequest): Promise<ApiResponse<ExecutePromotionResponse>> {
    return client.request<ExecutePromotionResponse>('/enrollments/promotion/execute', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listAttendance(params?: ListAttendanceQuery): Promise<ApiResponse<ListAttendanceResponse>> {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    if (params?.student_id) qs.set('student_id', params.student_id);
    if (params?.from_date) qs.set('from_date', params.from_date);
    if (params?.to_date) qs.set('to_date', params.to_date);
    const query = qs.toString();
    return client.request<ListAttendanceResponse>(query ? `/attendance?${query}` : '/attendance');
  },

async updateStudent(body: UpdateStudentRequest): Promise<ApiResponse<void>> {
    return client.request<void>('/students/update', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async softDeleteStudent(body: SoftDeleteStudentRequest): Promise<ApiResponse<void>> {
    return client.request<void>('/students/delete', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async createStudentDocument(body: CreateStudentDocumentRequest): Promise<ApiResponse<CreateStudentDocumentResponse>> {
    return client.request<CreateStudentDocumentResponse>('/students/documents', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listStudentDocuments(studentId: string, params?: { page?: number; limit?: number }) {
    const qs = new URLSearchParams();
    qs.set('student_id', studentId);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    return client.request<ListStudentDocumentsResponse>(`/students/documents?${qs.toString()}`);
  },

async createFeeWaiverApplication(
    body: CreateFeeWaiverApplicationRequest
  ): Promise<ApiResponse<CreateFeeWaiverApplicationResponse>> {
    return client.request<CreateFeeWaiverApplicationResponse>('/students/fee-waivers', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listFeeWaiverApplications(
    studentId?: string | null,
    params?: { page?: number; limit?: number; status?: 'pending' | 'approved' | 'rejected' }
  ) {
    const qs = new URLSearchParams();
    if (studentId) qs.set('student_id', studentId);
    if (params?.status) qs.set('status', params.status);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return client.request<ListFeeWaiverApplicationsResponse>(
      query ? `/students/fee-waivers?${query}` : '/students/fee-waivers'
    );
  },

async approveFeeWaiverApplication(
    body: ReviewFeeWaiverApplicationRequest
  ): Promise<ApiResponse<ReviewFeeWaiverApplicationResponse>> {
    return client.request<ReviewFeeWaiverApplicationResponse>('/students/fee-waivers/approve', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async rejectFeeWaiverApplication(
    body: ReviewFeeWaiverApplicationRequest
  ): Promise<ApiResponse<ReviewFeeWaiverApplicationResponse>> {
    return client.request<ReviewFeeWaiverApplicationResponse>('/students/fee-waivers/reject', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listStudentAudits(studentId: string, params?: { page?: number; limit?: number }) {
    const qs = new URLSearchParams();
    qs.set('student_id', studentId);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    return client.request<ListStudentAuditsResponse>(`/students/audits?${qs.toString()}`);
  },

async createGuardian(body: CreateGuardianRequest): Promise<ApiResponse<CreateGuardianResponse>> {
    return client.request<CreateGuardianResponse>('/guardians', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listGuardians(params?: { student_id?: string; page?: number; limit?: number }) {
    const qs = new URLSearchParams();
    if (params?.student_id) qs.set('student_id', params.student_id);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return client.request<ListGuardiansResponse>(query ? `/guardians?${query}` : '/guardians');
  },

async listStudentSubjects(params: {
  student_id: string;
  academic_year?: string;
}): Promise<ApiResponse<ListStudentSubjectsResponse>> {
    const qs = new URLSearchParams();
    qs.set('student_id', params.student_id);
    if (params.academic_year) qs.set('academic_year', params.academic_year);
    return client.request<ListStudentSubjectsResponse>(`/students/subjects?${qs.toString()}`);
  },

async setStudentSubjects(body: SetStudentSubjectsRequest): Promise<ApiResponse<SetStudentSubjectsResponse>> {
    return client.request<SetStudentSubjectsResponse>('/students/subjects', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  };
}
